import type { AutocompleteRenderGroupParams } from "@mui/material";
import { TextField } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { GroupHeader, GroupItems } from "../GroupedAutocomplete/groupedAutocomplete.styles";
import {
  ChipContainer,
  SelectionLabel,
  StyledAutocomplete,
  StyledCheckbox,
  StyledPopper,
  StyledSearchIcon,
} from "./multiSelectChip.styles";

export interface MultiSelectChipProps<T> {
  /** The label displayed on the chip */
  label: string;

  /** Array of available options */
  options: T[];

  /** Currently selected items (controlled mode) */
  value?: T[];

  /** Callback when selections change */
  onChange?: (selected: T[]) => void;

  /** Function to extract the display label from an option */
  getOptionLabel: (option: T) => string;

  /** Function to extract a unique key from an option */
  getOptionKey: (option: T) => string | number;

  /** Placeholder text for the search input */
  placeholder?: string;

  /** Custom icon displayed when no selections */
  icon?: React.ReactElement;

  /** Whether the chip is disabled */
  disabled?: boolean;

  /** Custom label for the "Assigned" group header */
  assignedGroupLabel?: string;

  /** Custom label for the "Available" group header */
  availableGroupLabel?: string;
}

export const MultiSelectChip = <T,>({
  label,
  options,
  value = [],
  onChange,
  getOptionLabel,
  getOptionKey,
  placeholder = "Search...",
  icon,
  disabled = false,
  assignedGroupLabel = "Assigned",
  availableGroupLabel = "Available",
}: MultiSelectChipProps<T>) => {
  // Ref for the chip element to anchor the autocomplete
  const chipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  /**
   * Track how many times the autocomplete has been opened.
   * Used to determine when to activate dynamic grouping.
   * Groups appear on the second opening when selections exist, or on the first
   * opening if items are pre-selected (initialized to 1 in that case).
   */
  const openCountRef = useRef(value.length > 0 ? 1 : 0);

  /**
   * Refs for scroll position preservation.
   * Maintains user's scroll context when items move between groups.
   */
  const listboxRef = useRef<HTMLElement | null>(null);
  const scrollPositionRef = useRef(0);

  const handleToggle = () => {
    if (!disabled) {
      if (!open) {
        openCountRef.current += 1;
      }
      setOpen((v) => !v);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!disabled && event.key === " ") {
      event.preventDefault();
      handleToggle();
    }
    // Enter is handled by onClick automatically by MUI Chip
  };

  /**
   * Determine if grouping should be active.
   * Groups appear when:
   * 1. Autocomplete has been opened at least twice (openCountRef.current >= 2)
   * 2. At least one item is currently selected (value.length > 0)
   */
  const shouldUseGrouping = openCountRef.current >= 2 && value.length > 0;

  /**
   * Memoized function to determine which group an option belongs to.
   * Returns 'assigned' for selected items, 'available' for unselected items.
   * Returns empty string when grouping is not active (flat list).
   */
  const getGroupKey = useMemo(
    () =>
      (option: T): string => {
        if (!shouldUseGrouping) return "";

        const isSelected = value.some((v) => getOptionKey(v) === getOptionKey(option));
        return isSelected ? "assigned" : "available";
      },
    [shouldUseGrouping, value, getOptionKey],
  );

  /**
   * Sort options to prevent duplicate group headers warning from MUI.
   * When grouping is active, sort by group (assigned first, then available).
   */
  const sortedOptions = useMemo(() => {
    if (!shouldUseGrouping) return options;

    return [...options].sort((a, b) => {
      const groupA = getGroupKey(a);
      const groupB = getGroupKey(b);

      // 'assigned' should come before 'available' alphabetically
      if (groupA < groupB) return -1;
      if (groupA > groupB) return 1;
      return 0;
    });
  }, [options, shouldUseGrouping, getGroupKey]);

  /**
   * Custom render function for group headers.
   * Maps group keys to display labels.
   */
  const renderGroupHeader = (params: AutocompleteRenderGroupParams) => {
    const groupLabel = params.group === "assigned" ? assignedGroupLabel : availableGroupLabel;
    return (
      <li key={params.key}>
        <GroupHeader>{groupLabel}</GroupHeader>
        <GroupItems>{params.children}</GroupItems>
      </li>
    );
  };

  /**
   * Handler for selection changes.
   * Preserves scroll position before state update to prevent disorienting jumps.
   */
  const handleChange = (_e: React.SyntheticEvent, newValue: T[]) => {
    // Capture current scroll position before group changes
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
    }

    onChange?.(newValue);
  };

  /**
   * Effect to restore scroll position after selections change.
   * Runs when value changes and groups re-render.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: value array identity changes trigger this effect
  useEffect(() => {
    if (listboxRef.current && scrollPositionRef.current > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [value]);

  // Format the chip label
  const getChipLabel = () => {
    if (value.length === 0) {
      return label;
    }

    const displayItems = value.slice(0, 2);
    const remaining = value.length - 2;

    const itemText = displayItems.map(getOptionLabel).join(", ");
    const moreText = remaining > 0 ? `, + ${remaining} more` : "";

    return (
      <SelectionLabel>
        <span className="label-text">{label}:</span>
        <span className="items-text">
          {itemText}
          {moreText}
        </span>
      </SelectionLabel>
    );
  };

  const hasSelections = value.length > 0;

  return (
    <>
      <ChipContainer
        ref={chipRef}
        icon={hasSelections ? undefined : icon}
        label={getChipLabel()}
        variant="outlined"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        hasSelections={hasSelections}
        isOpen={open}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-label={
          hasSelections
            ? `${label}: ${value.length} item${value.length === 1 ? "" : "s"} selected. Press Enter to change.`
            : `${label}: No items selected. Press Enter to select items.`
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      />
      <StyledPopper
        open={open}
        anchorEl={chipRef.current}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[
          { name: "flip", enabled: false },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              tether: false,
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
          {
            name: "computeStyles",
            options: {
              adaptive: false, // 👈 prevents horizontal jitter on content change
            },
          },
        ]}
      >
        <StyledAutocomplete
          multiple
          open={open}
          onClose={(_, reason) => {
            // Don't close when clicking inside the autocomplete
            if (reason === "escape" || reason === "blur") {
              setOpen(false);
            }
          }}
          disableCloseOnSelect
          disablePortal
          options={sortedOptions}
          value={value}
          onChange={handleChange}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(a, b) => getOptionKey(a) === getOptionKey(b)}
          groupBy={shouldUseGrouping ? getGroupKey : undefined}
          renderGroup={shouldUseGrouping ? renderGroupHeader : undefined}
          renderOption={(props, option, { selected }) => {
            // Create unique key combining group and option to prevent duplicates
            const uniqueKey = `${getGroupKey(option)}-${String(getOptionKey(option))}`;
            return (
              <li {...props} key={uniqueKey}>
                <StyledCheckbox checked={selected} />
                {getOptionLabel(option)}
              </li>
            );
          }}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              autoFocus
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <StyledSearchIcon />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          slotProps={{
            paper: { sx: { mt: 0 } },
            listbox: {
              ref: listboxRef,
              sx: {
                maxHeight: 280,
                "& .MuiAutocomplete-option": {
                  minHeight: "auto",
                  padding: "0px 16px",
                },
              },
            },
          }}
        />
      </StyledPopper>
    </>
  );
};
